package com.havn.app.features.openhouses.presentation

import com.havn.app.core.billing.HavnPlanLimits
import com.havn.app.core.billing.HavnPlans
import com.havn.app.core.logging.HavnLogger
import com.havn.app.core.presentation.havnFriendlyMessage
import com.havn.app.features.agency.domain.AgencyRepository
import com.havn.app.features.agency.domain.HavnSubscriptionTier
import com.havn.app.features.auth.domain.AuthRepository
import com.havn.app.features.auth.domain.AuthState
import com.havn.app.features.clients.domain.Client
import com.havn.app.features.clients.domain.ClientRepository
import com.havn.app.features.openhouses.domain.KioskSession
import com.havn.app.features.openhouses.domain.LeadTemperature
import com.havn.app.features.openhouses.domain.OpenHouse
import com.havn.app.features.openhouses.domain.OpenHouseDraft
import com.havn.app.features.openhouses.domain.OpenHousePropertyOption
import com.havn.app.features.openhouses.domain.OpenHouseRepository
import com.havn.app.features.openhouses.domain.VisitorInterest
import com.havn.app.features.openhouses.domain.VisitorSignInDraft
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import kotlin.time.Clock
import kotlin.random.Random

class OpenHousesViewModel(
    private val openHouseRepository: OpenHouseRepository,
    private val clientRepository: ClientRepository,
    private val authRepository: AuthRepository,
    private val agencyRepository: AgencyRepository,
    private val scope: CoroutineScope = CoroutineScope(SupervisorJob() + Dispatchers.Main)
) {
    private val _uiState = MutableStateFlow(OpenHousesUiState())
    val uiState: StateFlow<OpenHousesUiState> = _uiState.asStateFlow()

    private var agentId: String? = null
    private var agentEmail: String? = null
    private var planLimits: HavnPlanLimits = HavnPlans.freeLimits
    private var onOpenHouseScheduled: (() -> Unit)? = null
    private var selectedAgencyId: String? = null
    private var actAsAgent = false
    private var hasLoadedAgencyContext = false

    init {
        scope.launch {
            authRepository.authState.collect { state ->
                val user = (state as? AuthState.LoggedIn)?.user
                agentId = user?.id
                agentEmail = user?.email
                if (user == null) {
                    hasLoadedAgencyContext = false
                    _uiState.value = OpenHousesUiState()
                } else {
                    openHouseRepository.ensureAgentProfile(user.id, user.email)
                        .onFailure { setError(it) }
                    loadPlanLimits(user.id)
                    if (!hasLoadedAgencyContext) {
                        hasLoadedAgencyContext = true
                        selectedAgencyId = null
                    }
                    load(user.id)
                }
            }
        }
    }

    fun refresh() {
        val currentAgentId = agentId ?: return
        scope.launch { load(currentAgentId) }
    }

    fun refreshEntitlement(tier: HavnSubscriptionTier) {
        val currentAgentId = agentId ?: return
        planLimits = HavnPlans.limitsFor(tier)
        scope.launch {
            load(currentAgentId)
        }
    }

    fun updateSearchQuery(query: String) {
        if (_uiState.value.searchQuery == query) return
        _uiState.value = _uiState.value.copy(searchQuery = query, error = null)
        val currentAgentId = agentId ?: return
        scope.launch { load(currentAgentId) }
    }

    fun loadMoreOpenHouses() {
        val currentAgentId = agentId ?: return
        val state = _uiState.value
        if (state.isLoading || state.isLoadingMore || !state.hasMoreOpenHouses || state.openHouses.size >= planLimits.maxOpenings) return
        scope.launch { load(currentAgentId, append = true) }
    }

    fun loadOpenHousesForProperty(propertyId: String) {
        val currentAgentId = agentId ?: return
        scope.launch {
            openHouseRepository.openHousesForProperty(currentAgentId, propertyId)
                .onSuccess { propertyOpenings ->
                    mergeOpenHouses(propertyOpenings)
                }
                .onFailure { setError(it) }
        }
    }

    fun setAgencyContext(agencyId: String?, nextActAsAgent: Boolean = false) {
        if (hasLoadedAgencyContext && selectedAgencyId == agencyId && actAsAgent == nextActAsAgent) return
        selectedAgencyId = agencyId
        actAsAgent = nextActAsAgent
        hasLoadedAgencyContext = true
        val currentAgentId = agentId ?: return
        scope.launch { load(currentAgentId) }
    }

    fun onOpenHouseScheduled(callback: () -> Unit) {
        onOpenHouseScheduled = callback
    }

    fun showList() {
        _uiState.value = _uiState.value.copy(
            mode = OpenHousesMode.List,
            selectedOpenHouseId = null,
            returnToPropertyId = null
        )
    }

    fun showSchedule() {
        val state = _uiState.value
        val firstPropertyId = state.propertyOptions.firstOrNull { property ->
            state.openingCountForProperty(property.id) < planLimits.maxOpeningsPerProperty
        }?.id
        if (firstPropertyId == null) {
            _uiState.value = state.copy(error = "Your current plan includes up to ${planLimits.maxOpeningsPerProperty} open houses per property.")
            return
        }
        _uiState.value = _uiState.value.copy(
            mode = OpenHousesMode.Schedule,
            selectedOpenHouseId = null,
            returnToPropertyId = null,
            form = OpenHouseFormState.defaultFor(firstPropertyId)
        )
    }

    fun showScheduleForProperty(propertyId: String) {
        val state = _uiState.value
        if (state.openingCountForProperty(propertyId) >= planLimits.maxOpeningsPerProperty) {
            _uiState.value = state.copy(error = "Your current plan includes up to ${planLimits.maxOpeningsPerProperty} open houses for each property.")
            return
        }
        _uiState.value = state.copy(
            mode = OpenHousesMode.Schedule,
            selectedOpenHouseId = null,
            returnToPropertyId = propertyId,
            form = OpenHouseFormState.defaultFor(propertyId)
        )
    }

    fun clearPropertyReturn() {
        _uiState.value = _uiState.value.copy(returnToPropertyId = null)
    }

    fun openDetail(openHouseId: String) {
        val currentAgentId = agentId ?: return
        _uiState.value = _uiState.value.copy(
            mode = OpenHousesMode.Detail,
            selectedOpenHouseId = openHouseId,
            isDetailLoading = true,
            error = null
        )
        scope.launch {
            openHouseRepository.openHouseDetail(currentAgentId, openHouseId)
                .onSuccess {
                    replaceOpenHouse(it)
                    _uiState.value = _uiState.value.copy(isDetailLoading = false)
                }
                .onFailure {
                    _uiState.value = _uiState.value.copy(isDetailLoading = false)
                    setError(it)
                }
        }
    }

    fun showExistingDetail(openHouseId: String) {
        _uiState.value = _uiState.value.copy(
            mode = OpenHousesMode.Detail,
            selectedOpenHouseId = openHouseId,
            isDetailLoading = false,
            error = null
        )
    }

    fun showLiveDashboard(openHouseId: String) {
        _uiState.value = _uiState.value.copy(mode = OpenHousesMode.LiveDashboard, selectedOpenHouseId = openHouseId)
        openDetail(openHouseId)
        _uiState.value = _uiState.value.copy(mode = OpenHousesMode.LiveDashboard, selectedOpenHouseId = openHouseId)
    }

    fun updateForm(transform: OpenHouseFormState.() -> OpenHouseFormState) {
        _uiState.value = _uiState.value.copy(form = _uiState.value.form.transform(), error = null)
    }

    fun scheduleOpenHouse() {
        val currentAgentId = agentId ?: return
        val state = _uiState.value
        if (state.openHouses.size >= planLimits.maxOpenings) {
            _uiState.value = state.copy(
                isSaving = false,
                error = "Your current plan includes up to ${planLimits.maxOpenings} open houses. Upgrade to add more."
            )
            return
        }
        if (state.openingCountForProperty(state.form.propertyId) >= planLimits.maxOpeningsPerProperty) {
            _uiState.value = state.copy(
                isSaving = false,
                error = "Your current plan includes up to ${planLimits.maxOpeningsPerProperty} open houses for each property."
            )
            return
        }
        val validation = state.form.validate()
        if (validation != null) {
            _uiState.value = state.copy(form = state.form.copy(validationError = validation))
            return
        }

        _uiState.value = state.copy(isSaving = true, error = null)
        scope.launch {
            openHouseRepository.scheduleOpenHouse(currentAgentId, state.form.toDraft(), selectedAgencyId)
                .onSuccess { openHouse ->
                    onOpenHouseScheduled?.invoke()
                    replaceOpenHouse(openHouse)
                    _uiState.value = _uiState.value.copy(
                        mode = OpenHousesMode.Detail,
                        selectedOpenHouseId = openHouse.id,
                        isSaving = false,
                        form = OpenHouseFormState()
                    )
                }
                .onFailure {
                    _uiState.value = _uiState.value.copy(isSaving = false)
                    setError(it)
                }
        }
    }

    fun startEdit() {
        val selected = _uiState.value.selectedOpenHouse ?: return
        _uiState.value = _uiState.value.copy(
            mode = OpenHousesMode.Edit,
            form = selected.toFormState(),
            error = null
        )
    }

    fun cancelForm() {
        val selectedId = _uiState.value.selectedOpenHouseId
        _uiState.value = _uiState.value.copy(
            mode = if (selectedId == null) OpenHousesMode.List else OpenHousesMode.Detail,
            form = OpenHouseFormState(),
            error = null
        )
    }

    fun saveEditedOpenHouse() {
        val currentAgentId = agentId ?: return
        val openHouseId = _uiState.value.selectedOpenHouseId ?: return
        val state = _uiState.value
        val validation = state.form.validate()
        if (validation != null) {
            _uiState.value = state.copy(form = state.form.copy(validationError = validation))
            return
        }

        _uiState.value = state.copy(isSaving = true, error = null)
        scope.launch {
            openHouseRepository.updateOpenHouse(currentAgentId, openHouseId, state.form.toDraft())
                .onSuccess { openHouse ->
                    replaceOpenHouse(openHouse)
                    _uiState.value = _uiState.value.copy(
                        mode = OpenHousesMode.Detail,
                        selectedOpenHouseId = openHouse.id,
                        isSaving = false,
                        form = OpenHouseFormState()
                    )
                }
                .onFailure {
                    _uiState.value = _uiState.value.copy(isSaving = false)
                    setError(it)
                }
        }
    }

    fun requestDeleteSelected() {
        if (_uiState.value.selectedOpenHouseId == null) return
        _uiState.value = _uiState.value.copy(showDeleteConfirmation = true, error = null)
    }

    fun dismissDeleteConfirmation() {
        _uiState.value = _uiState.value.copy(showDeleteConfirmation = false)
    }

    fun confirmDeleteSelected() {
        val currentAgentId = agentId ?: return
        val openHouseId = _uiState.value.selectedOpenHouseId ?: return
        _uiState.value = _uiState.value.copy(isSaving = true, showDeleteConfirmation = false, error = null)
        scope.launch {
            openHouseRepository.deleteOpenHouse(currentAgentId, openHouseId)
                .onSuccess {
                    _uiState.value = _uiState.value.copy(
                        openHouses = _uiState.value.openHouses.filterNot { it.id == openHouseId },
                        selectedOpenHouseId = null,
                        mode = OpenHousesMode.List,
                        isSaving = false,
                        form = OpenHouseFormState()
                    )
                }
                .onFailure {
                    _uiState.value = _uiState.value.copy(isSaving = false)
                    setError(it)
                }
        }
    }

    fun startSelectedOpenHouse() {
        updateSelectedStatus(start = true)
    }

    fun startOpenHouse(openHouseId: String) {
        _uiState.value = _uiState.value.copy(selectedOpenHouseId = openHouseId)
        updateSelectedStatus(start = true)
    }

    fun endSelectedOpenHouse() {
        updateSelectedStatus(start = false)
    }

    fun generateKioskSession() {
        val selected = _uiState.value.selectedOpenHouse ?: return
        val code = List(6) { Random.nextInt(0, 10) }.joinToString("")
        _uiState.value = _uiState.value.copy(
            kioskSession = KioskSession(
                openHouseId = selected.id,
                code = code,
                kioskPath = "havn://kiosk/open-house/${selected.id}?code=$code"
            )
        )
    }

    fun launchKioskMode() {
        val selected = _uiState.value.selectedOpenHouse ?: return
        val currentSession = _uiState.value.kioskSession?.takeIf { it.openHouseId == selected.id }
        if (currentSession == null) generateKioskSession()
        _uiState.value = _uiState.value.copy(
            mode = OpenHousesMode.VisitorKiosk,
            kioskStep = KioskStep.Welcome,
            visitorForm = VisitorKioskFormState(),
            qrScanPayload = "",
            qrScanPayloads = emptyList(),
            qrScanMessage = null,
            qrRecognitionEventId = 0,
            kioskError = null,
            error = null
        )
    }

    fun showQrScanner() {
        val selected = _uiState.value.selectedOpenHouse ?: return
        _uiState.value = _uiState.value.copy(
            mode = OpenHousesMode.ScanQr,
            selectedOpenHouseId = selected.id,
            qrScanPayload = "",
            qrScanPayloads = emptyList(),
            qrScanMessage = null,
            clientSearchQuery = "",
            kioskError = null,
            error = null
        )
    }

    fun showClientSelector() {
        val selected = _uiState.value.selectedOpenHouse ?: return
        _uiState.value = _uiState.value.copy(
            mode = OpenHousesMode.SelectClients,
            selectedOpenHouseId = selected.id,
            clientSearchQuery = "",
            manualLeadTemperature = LeadTemperature.Warm,
            clientSelectorClients = emptyList(),
            isLoadingClientSelector = true,
            isLoadingMoreClientSelector = false,
            hasMoreClientSelectorClients = false,
            qrScanMessage = null,
            kioskError = null,
            error = null
        )
        loadClientSelectorPage()
    }

    fun updateQrScanPayload(value: String) {
        _uiState.value = _uiState.value.copy(qrScanPayload = value, qrScanMessage = null, kioskError = null, error = null)
    }

    fun submitQrScan() {
        val payloads = _uiState.value.qrScanPayloads.ifEmpty {
            listOfNotNull(_uiState.value.qrScanPayload.takeIf { it.isNotBlank() })
        }
        submitQrPayloads(payloads)
    }

    fun submitScannedQrPayload(payload: String) {
        if (payload.isBlank()) return
        val state = _uiState.value
        if (payload in state.qrScanPayloads) {
            _uiState.value = state.copy(qrScanMessage = "Contact QR already scanned.", kioskError = null, error = null)
            return
        }
        _uiState.value = state.copy(
            qrScanPayload = payload,
            qrScanPayloads = state.qrScanPayloads + payload,
            qrScanMessage = "Contact QR scanned. Tap Add QR Contact when ready.",
            kioskError = null,
            error = null
        )
    }

    fun checkInScannedQrPayload(payload: String) {
        if (_uiState.value.isSaving) return
        submitQrPayloads(listOf(payload))
    }

    fun removeScannedQrPayload(payload: String) {
        _uiState.value = _uiState.value.copy(
            qrScanPayloads = _uiState.value.qrScanPayloads.filterNot { it == payload },
            qrScanMessage = null,
            kioskError = null,
            error = null
        )
    }

    private fun submitQrPayloads(payloads: List<String>) {
        val currentAgentId = agentId ?: return
        val openHouseId = _uiState.value.selectedOpenHouseId ?: return
        val uniquePayloads = payloads.map { it.trim() }.filter { it.isNotBlank() }.distinct()
        if (uniquePayloads.isEmpty()) {
            _uiState.value = _uiState.value.copy(kioskError = "Scan a HAVN contact QR first.")
            return
        }

        _uiState.value = _uiState.value.copy(isSaving = true, kioskError = null, qrScanMessage = null)
        scope.launch {
            var latestOpenHouse: OpenHouse? = null
            var createdCount = 0
            var alreadyCheckedInCount = 0
            var firstError: Throwable? = null
            val alreadyVisibleContactIds = _uiState.value.selectedOpenHouse
                ?.recentSignIns
                ?.mapNotNull { it.clientId }
                ?.toSet()
                .orEmpty()
            for (payload in uniquePayloads) {
                openHouseRepository.checkInClientQr(currentAgentId, openHouseId, payload)
                    .onSuccess { result ->
                        latestOpenHouse = result.openHouse
                        if (!result.leadCreated || result.contactId in alreadyVisibleContactIds) {
                            alreadyCheckedInCount += 1
                        } else {
                            createdCount += 1
                        }
                    }
                    .onFailure {
                        firstError = it
                    }
                if (firstError != null) break
            }
            latestOpenHouse?.let { replaceOpenHouse(it) }
            if (firstError == null) {
                _uiState.value = _uiState.value.copy(
                    isSaving = false,
                    qrScanPayload = "",
                    qrScanPayloads = emptyList(),
                    qrScanMessage = when {
                        createdCount > 0 && alreadyCheckedInCount > 0 ->
                            "$createdCount QR contact${if (createdCount == 1) "" else "s"} added as lead${if (createdCount == 1) "" else "s"}. $alreadyCheckedInCount already checked in."
                        createdCount > 0 ->
                            "$createdCount QR contact${if (createdCount == 1) "" else "s"} added as lead${if (createdCount == 1) "" else "s"}."
                        else ->
                            "Contact already checked in."
                    },
                    qrRecognitionEventId = if (createdCount > 0 || alreadyCheckedInCount > 0) {
                        _uiState.value.qrRecognitionEventId + 1
                    } else {
                        _uiState.value.qrRecognitionEventId
                    }
                )
            } else {
                _uiState.value = _uiState.value.copy(isSaving = false)
                setError(firstError)
                _uiState.value = _uiState.value.copy(kioskError = _uiState.value.error)
            }
        }
    }

    fun updateClientSearchQuery(value: String) {
        if (_uiState.value.clientSearchQuery == value) return
        _uiState.value = _uiState.value.copy(
            clientSearchQuery = value,
            clientSelectorClients = emptyList(),
            isLoadingClientSelector = true,
            isLoadingMoreClientSelector = false,
            hasMoreClientSelectorClients = false,
            kioskError = null,
            error = null
        )
        loadClientSelectorPage()
    }

    fun updateManualLeadTemperature(temperature: LeadTemperature) {
        _uiState.value = _uiState.value.copy(manualLeadTemperature = temperature, kioskError = null, error = null)
    }

    fun loadMoreClientSelectorClients() {
        val state = _uiState.value
        if (
            state.mode != OpenHousesMode.SelectClients ||
            state.isLoadingClientSelector ||
            state.isLoadingMoreClientSelector ||
            !state.hasMoreClientSelectorClients
        ) return
        loadClientSelectorPage(append = true)
    }

    fun checkInClient(clientId: String) {
        val currentAgentId = agentId ?: return
        val openHouseId = _uiState.value.selectedOpenHouseId ?: return
        val client = (_uiState.value.clientSelectorClients + _uiState.value.clients).firstOrNull { it.id == clientId } ?: return
        val selectedTemperature = _uiState.value.manualLeadTemperature

        _uiState.value = _uiState.value.copy(isSaving = true, kioskError = null, qrScanMessage = null)
        scope.launch {
            openHouseRepository.checkInClientQr(
                currentAgentId,
                openHouseId,
                client.qrPayload,
                selectedTemperature
            )
                .onSuccess { openHouse ->
                    replaceOpenHouse(openHouse.openHouse)
                    _uiState.value = _uiState.value.copy(
                        isSaving = false,
                        qrScanMessage = if (openHouse.leadCreated) {
                            "${client.fullName} checked in and lead created."
                        } else {
                            "${client.fullName} is already checked in."
                        },
                        clientSearchQuery = ""
                    )
                }
                .onFailure {
                    _uiState.value = _uiState.value.copy(isSaving = false)
                    setError(it)
                    _uiState.value = _uiState.value.copy(kioskError = _uiState.value.error)
                }
        }
    }

    fun requestRemoveClient(clientId: String) {
        val state = _uiState.value
        val visitor = state.selectedOpenHouse?.recentSignIns?.firstOrNull { it.clientId == clientId }
        val client = (state.clientSelectorClients + state.clients).firstOrNull { it.id == clientId }
        _uiState.value = state.copy(
            pendingRemoveLeadClientId = clientId,
            pendingRemoveLeadName = visitor?.fullName ?: client?.fullName,
            error = null,
            kioskError = null
        )
    }

    fun dismissRemoveLeadConfirmation() {
        _uiState.value = _uiState.value.copy(
            pendingRemoveLeadClientId = null,
            pendingRemoveLeadName = null
        )
    }

    fun confirmRemoveLead() {
        val clientId = _uiState.value.pendingRemoveLeadClientId ?: return
        _uiState.value = _uiState.value.copy(
            pendingRemoveLeadClientId = null,
            pendingRemoveLeadName = null
        )
        removeClient(clientId)
    }

    private fun removeClient(clientId: String) {
        val currentAgentId = agentId ?: return
        val openHouseId = _uiState.value.selectedOpenHouseId ?: return
        val client = (_uiState.value.clientSelectorClients + _uiState.value.clients).firstOrNull { it.id == clientId }

        _uiState.value = _uiState.value.copy(isSaving = true, kioskError = null, qrScanMessage = null)
        scope.launch {
            openHouseRepository.removeClientFromOpenHouse(currentAgentId, openHouseId, clientId)
                .onSuccess { openHouse ->
                    replaceOpenHouse(openHouse)
                    _uiState.value = _uiState.value.copy(
                        isSaving = false,
                        qrScanMessage = "${client?.fullName ?: "Contact"} removed from open house."
                    )
                }
                .onFailure {
                    _uiState.value = _uiState.value.copy(isSaving = false)
                    setError(it)
                    _uiState.value = _uiState.value.copy(kioskError = _uiState.value.error)
                }
        }
    }

    fun exitKioskMode() {
        val selectedId = _uiState.value.selectedOpenHouseId
        _uiState.value = _uiState.value.copy(
            mode = if (selectedId == null) OpenHousesMode.List else OpenHousesMode.Detail,
            kioskStep = KioskStep.Welcome,
            visitorForm = VisitorKioskFormState(),
            kioskError = null
        )
    }

    fun updateVisitorForm(transform: VisitorKioskFormState.() -> VisitorKioskFormState) {
        _uiState.value = _uiState.value.copy(
            visitorForm = _uiState.value.visitorForm.transform(),
            kioskError = null
        )
    }

    fun nextKioskStep() {
        val state = _uiState.value
        val next = when (state.kioskStep) {
            KioskStep.Welcome -> KioskStep.Contact
            KioskStep.Contact -> {
                val validation = state.visitorForm.validateContact()
                if (validation != null) {
                    _uiState.value = state.copy(kioskError = validation)
                    return
                }
                KioskStep.Questions
            }
            KioskStep.Questions -> KioskStep.Questions
            KioskStep.ThankYou -> KioskStep.Welcome
        }
        _uiState.value = state.copy(kioskStep = next, kioskError = null)
    }

    fun previousKioskStep() {
        val previous = when (_uiState.value.kioskStep) {
            KioskStep.Welcome -> KioskStep.Welcome
            KioskStep.Contact -> KioskStep.Welcome
            KioskStep.Questions -> KioskStep.Contact
            KioskStep.ThankYou -> KioskStep.Welcome
        }
        _uiState.value = _uiState.value.copy(kioskStep = previous, kioskError = null)
    }

    fun submitVisitorSignIn() {
        val currentAgentId = agentId ?: return
        val openHouseId = _uiState.value.selectedOpenHouseId ?: return
        val state = _uiState.value
        val validation = state.visitorForm.validateQuestions()
        if (validation != null) {
            _uiState.value = state.copy(kioskError = validation)
            return
        }

        _uiState.value = state.copy(isSaving = true, kioskError = null)
        scope.launch {
            openHouseRepository.submitVisitorSignIn(
                agentId = currentAgentId,
                openHouseId = openHouseId,
                draft = state.visitorForm.toDraft()
            ).onSuccess { openHouse ->
                replaceOpenHouse(openHouse)
                _uiState.value = _uiState.value.copy(
                    isSaving = false,
                    kioskStep = KioskStep.ThankYou,
                    visitorForm = VisitorKioskFormState()
                )
                delay(5_000)
                if (_uiState.value.mode == OpenHousesMode.VisitorKiosk && _uiState.value.kioskStep == KioskStep.ThankYou) {
                    _uiState.value = _uiState.value.copy(kioskStep = KioskStep.Welcome)
                }
            }.onFailure {
                _uiState.value = _uiState.value.copy(isSaving = false)
                setError(it)
                _uiState.value = _uiState.value.copy(kioskError = _uiState.value.error)
            }
        }
    }

    fun clearError() {
        _uiState.value = _uiState.value.copy(error = null)
    }

    fun dispose() {
        scope.cancel()
    }

    private fun updateSelectedStatus(start: Boolean) {
        val currentAgentId = agentId ?: return
        val openHouseId = _uiState.value.selectedOpenHouseId ?: return
        _uiState.value = _uiState.value.copy(isSaving = true, error = null)
        scope.launch {
            val result = if (start) {
                openHouseRepository.startOpenHouse(currentAgentId, openHouseId)
            } else {
                openHouseRepository.endOpenHouse(currentAgentId, openHouseId)
            }
            result
                .onSuccess {
                    replaceOpenHouse(it)
                    _uiState.value = _uiState.value.copy(
                        mode = if (start) OpenHousesMode.LiveDashboard else OpenHousesMode.Detail,
                        selectedOpenHouseId = it.id,
                        isSaving = false
                    )
                }
                .onFailure {
                    _uiState.value = _uiState.value.copy(isSaving = false)
                    setError(it)
                }
        }
    }

    private suspend fun load(currentAgentId: String, append: Boolean = false) {
        val state = _uiState.value
        val visibleLimit = planLimits.maxOpenings
        if (append && state.openHouses.size >= visibleLimit) {
            _uiState.value = state.copy(hasMoreOpenHouses = false)
            return
        }
        val offset = if (append) state.openHouses.size else 0
        val pageLimit = if (visibleLimit == Int.MAX_VALUE) OPEN_HOUSE_PAGE_SIZE else (visibleLimit - offset).coerceAtMost(OPEN_HOUSE_PAGE_SIZE)
        if (pageLimit <= 0) {
            _uiState.value = state.copy(isLoading = false, isLoadingMore = false, hasMoreOpenHouses = false)
            return
        }
        _uiState.value = if (append) {
            state.copy(isLoadingMore = true, error = null)
        } else {
            state.copy(isLoading = true, error = null)
        }
        val openHouses = openHouseRepository.openHousePage(
            agentId = currentAgentId,
            agencyId = selectedAgencyId,
            query = _uiState.value.searchQuery,
            offset = offset,
            limit = pageLimit,
            actAsAgent = actAsAgent
        )
        openHouses.onFailure { setError(it) }

        val pageOpenHouses = openHouses.getOrElse { emptyList() }.map { it.visibleForPlan() }
        val mergedOpenHouses = if (append) (_uiState.value.openHouses + pageOpenHouses).distinctBy { it.id } else pageOpenHouses
        val visibleOpenHouses = if (visibleLimit == Int.MAX_VALUE) mergedOpenHouses else mergedOpenHouses.take(visibleLimit)

        _uiState.value = _uiState.value.copy(
            isLoading = false,
            isLoadingMore = false,
            openHouses = visibleOpenHouses,
            hasMoreOpenHouses = pageOpenHouses.size == pageLimit && visibleOpenHouses.size < visibleLimit,
            hasLoadedInitialPage = _uiState.value.hasLoadedInitialPage || !append
        )

        val properties = openHouseRepository.propertyOptions(currentAgentId, selectedAgencyId)
        properties.onSuccess { propertyOptions ->
            _uiState.value = _uiState.value.copy(
                propertyOptions = propertyOptions.map { it.visibleForPlan() }.take(planLimits.visibleProperties),
                hasLoadedPropertyOptions = true
            )
        }.onFailure {
            _uiState.value = _uiState.value.copy(hasLoadedPropertyOptions = true)
            setError(it)
        }

        val clients = clientRepository.clientsForAgent(currentAgentId, selectedAgencyId)
        clients.onSuccess { loadedClients ->
            _uiState.value = _uiState.value.copy(clients = loadedClients)
        }.onFailure { setError(it) }
    }

    private fun loadClientSelectorPage(append: Boolean = false) {
        val currentAgentId = agentId ?: return
        val state = _uiState.value
        val offset = if (append) state.clientSelectorClients.size else 0
        val requestQuery = state.clientSearchQuery
        _uiState.value = if (append) {
            state.copy(isLoadingMoreClientSelector = true, kioskError = null, error = null)
        } else {
            state.copy(isLoadingClientSelector = true, kioskError = null, error = null)
        }
        scope.launch {
            clientRepository.clientPage(
                agentId = currentAgentId,
                agencyId = selectedAgencyId,
                query = requestQuery,
                offset = offset,
                limit = CLIENT_SELECTOR_PAGE_SIZE
            ).onSuccess { page ->
                if (_uiState.value.clientSearchQuery != requestQuery) return@onSuccess
                _uiState.value = _uiState.value.copy(
                    isLoadingClientSelector = false,
                    isLoadingMoreClientSelector = false,
                    clientSelectorClients = if (append) {
                        (_uiState.value.clientSelectorClients + page).distinctBy { it.id }
                    } else {
                        page
                    },
                    hasMoreClientSelectorClients = page.size == CLIENT_SELECTOR_PAGE_SIZE
                )
            }.onFailure {
                _uiState.value = _uiState.value.copy(
                    isLoadingClientSelector = false,
                    isLoadingMoreClientSelector = false
                )
                setError(it)
                _uiState.value = _uiState.value.copy(kioskError = _uiState.value.error)
            }
        }
    }

    private suspend fun loadPlanLimits(currentAgentId: String) {
        agencyRepository.entitlementForUser(currentAgentId)
            .onSuccess { entitlement ->
                planLimits = HavnPlans.limitsFor(entitlement.tier)
            }
            .onFailure {
                HavnLogger.error("OpenHousesViewModel", "Failed to load plan limits", it)
                planLimits = HavnPlans.freeLimits
            }
    }

    private fun replaceOpenHouse(openHouse: OpenHouse) {
        mergeOpenHouses(listOf(openHouse))
    }

    private fun mergeOpenHouses(openHouses: List<OpenHouse>) {
        val current = _uiState.value.openHouses
        val next = (openHouses.map { it.visibleForPlan() } + current).distinctBy { it.id }
            .sortedByDescending { it.startsAt }
        _uiState.value = _uiState.value.copy(openHouses = next)
    }

    private fun OpenHouse.visibleForPlan(): OpenHouse {
        val maxPhotos = planLimits.maxPhotosPerProperty.coerceAtLeast(0)
        if (propertyPhotoDataUrls.size <= maxPhotos) return this
        val visiblePhotos = propertyPhotoDataUrls.take(maxPhotos)
        return copy(
            propertyThumbnailDataUrl = visiblePhotos.firstOrNull() ?: propertyThumbnailDataUrl,
            propertyPhotoDataUrls = visiblePhotos
        )
    }

    private fun OpenHousePropertyOption.visibleForPlan(): OpenHousePropertyOption {
        val maxPhotos = planLimits.maxPhotosPerProperty.coerceAtLeast(0)
        if (photoDataUrls.size <= maxPhotos) return this
        val visiblePhotos = photoDataUrls.take(maxPhotos)
        return copy(
            thumbnailDataUrl = visiblePhotos.firstOrNull() ?: thumbnailDataUrl,
            photoDataUrls = visiblePhotos
        )
    }

    private fun setError(error: Throwable) {
        HavnLogger.error("OpenHousesViewModel", "User-visible open-house error", error)
        _uiState.value = _uiState.value.copy(
            error = error.havnFriendlyMessage("Could not load open houses. Please try again.")
        )
    }
}

private fun OpenHousesUiState.openingCountForProperty(propertyId: String): Int =
    openHouses.count { it.propertyId == propertyId }

data class OpenHousesUiState(
    val isLoading: Boolean = false,
    val isLoadingMore: Boolean = false,
    val hasLoadedInitialPage: Boolean = false,
    val hasLoadedPropertyOptions: Boolean = false,
    val isDetailLoading: Boolean = false,
    val isSaving: Boolean = false,
    val error: String? = null,
    val mode: OpenHousesMode = OpenHousesMode.List,
    val openHouses: List<OpenHouse> = emptyList(),
    val searchQuery: String = "",
    val hasMoreOpenHouses: Boolean = false,
    val propertyOptions: List<OpenHousePropertyOption> = emptyList(),
    val clients: List<Client> = emptyList(),
    val clientSelectorClients: List<Client> = emptyList(),
    val isLoadingClientSelector: Boolean = false,
    val isLoadingMoreClientSelector: Boolean = false,
    val hasMoreClientSelectorClients: Boolean = false,
    val selectedOpenHouseId: String? = null,
    val form: OpenHouseFormState = OpenHouseFormState(),
    val kioskSession: KioskSession? = null,
    val kioskStep: KioskStep = KioskStep.Welcome,
    val visitorForm: VisitorKioskFormState = VisitorKioskFormState(),
    val kioskError: String? = null,
    val qrScanPayload: String = "",
    val qrScanPayloads: List<String> = emptyList(),
    val qrScanMessage: String? = null,
    val qrRecognitionEventId: Int = 0,
    val clientSearchQuery: String = "",
    val manualLeadTemperature: LeadTemperature = LeadTemperature.Warm,
    val showDeleteConfirmation: Boolean = false,
    val pendingRemoveLeadClientId: String? = null,
    val pendingRemoveLeadName: String? = null,
    val returnToPropertyId: String? = null
) {
    val selectedOpenHouse: OpenHouse?
        get() = openHouses.firstOrNull { it.id == selectedOpenHouseId }

    val liveCount: Int
        get() = openHouses.count { it.status.databaseValue == "live" }

    val scheduledCount: Int
        get() = openHouses.count { it.status.databaseValue == "scheduled" }

    val visitorCount: Int
        get() = openHouses.sumOf { it.stats.visitorCount }

    val filteredClients: List<Client>
        get() = clientSelectorClients
}

enum class OpenHousesMode {
    List,
    Detail,
    Schedule,
    Edit,
    LiveDashboard,
    VisitorKiosk,
    ScanQr,
    SelectClients
}

private fun OpenHouse.toFormState(): OpenHouseFormState = OpenHouseFormState(
    propertyId = propertyId,
    title = title.orEmpty(),
    startsAt = startsAt,
    endsAt = endsAt,
    accessNotes = accessNotes.orEmpty(),
    internalNotes = internalNotes.orEmpty()
)

private const val OPEN_HOUSE_PAGE_SIZE = 20
private const val CLIENT_SELECTOR_PAGE_SIZE = 20

enum class KioskStep {
    Welcome,
    Contact,
    Questions,
    ThankYou
}

data class OpenHouseFormState(
    val propertyId: String = "",
    val title: String = "",
    val startsAt: String = "",
    val endsAt: String = "",
    val accessNotes: String = "",
    val internalNotes: String = "",
    val validationError: String? = null
) {
    fun toDraft(): OpenHouseDraft = OpenHouseDraft(
        propertyId = propertyId,
        title = title,
        startsAt = startsAt,
        endsAt = endsAt,
        accessNotes = accessNotes,
        internalNotes = internalNotes
    )

    fun validate(): String? = when {
        propertyId.isBlank() -> "Choose a property."
        startsAt.isBlank() -> "Start time is required."
        endsAt.isBlank() -> "End time is required."
        else -> null
    }

    companion object {
        fun defaultFor(propertyId: String): OpenHouseFormState {
            val date = Clock.System.now().toString().substringBefore("T")
            return OpenHouseFormState(
                propertyId = propertyId,
                startsAt = "${date}T16:00:00Z",
                endsAt = "${date}T18:00:00Z"
            )
        }
    }
}

data class VisitorKioskFormState(
    val fullName: String = "",
    val phone: String = "",
    val email: String = "",
    val workingWithAgent: Boolean? = null,
    val interest: VisitorInterest? = null
) {
    fun validateContact(): String? = when {
        fullName.isBlank() -> "Please enter your name."
        phone.isBlank() && email.isBlank() -> "Please enter a phone number or email."
        email.isNotBlank() && ("@" !in email || "." !in email.substringAfter("@", "")) -> "Please enter a valid email."
        else -> null
    }

    fun validateQuestions(): String? = validateContact() ?: when {
        workingWithAgent == null -> "Please choose whether you are working with an agent."
        interest == null -> "Please choose what you are interested in."
        else -> null
    }

    fun toDraft(): VisitorSignInDraft = VisitorSignInDraft(
        fullName = fullName,
        phone = phone,
        email = email,
        workingWithAgent = workingWithAgent == true,
        interest = interest ?: VisitorInterest.Browsing
    )
}
